/**
 * Essay detail page component.
 *
 * Features:
 * - Full essay content with markdown rendering
 * - Essay metadata (title, status, author, dates)
 * - Back navigation
 * - Loading and error states
 * - Inline comment popover on text selection
 * - Text highlighting for comments and suggestions
 * - Status dropdown for workflow transitions
 * - Table of contents sidebar
 * - Category and tags display
 * - Series navigation
 * - Action buttons linking to sub-pages (comments, suggestions, history, info)
 *
 * Called by:
 * - App.tsx as route for /reviewer/essays/:slug
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Typography, Tag, Space, Spin, Alert, Button, Divider, Avatar, Badge, Row, Col, message } from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  CalendarOutlined,
  EditOutlined,
  CommentOutlined,
  BulbOutlined,
  HistoryOutlined,
  PaperClipOutlined,
  FolderOutlined,
  TagsOutlined,
  OrderedListOutlined,
  SoundOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getEssay,
  getComments,
  getSuggestions,
  getAttachments,
  getLinkedNotes,
  getEssayTags,
  getEssaySeriesInfo,
  generateAudio,
} from '../services/api';
import TextHighlighter, { type TextSelection } from '../components/TextHighlighter';
import InlineCommentPopover from '../components/InlineCommentPopover';
import StatusDropdown from '../components/StatusDropdown';
import TableOfContents from '../components/TableOfContents';
import { useAuth } from '../hooks/useAuth';
import { useEssayEvents } from '../hooks/useEssayEvents';
import EssayEditor from '../components/EssayEditor';
import type { EssayStatus, CommentWithReplies, Essay, LinkedNoteFromEssay } from '@compendium/shared';

const { Title, Text } = Typography;

// =============================================================================
// Main Component
// =============================================================================

function EssayDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // UI state
  const [textSelection, setTextSelection] = useState<TextSelection | null>(null);

  // Editor state (bidirectional sync)
  const [isEditing, setIsEditing] = useState(false);
  const [editorRevision, setEditorRevision] = useState(0);
  const [externalUpdate, setExternalUpdate] = useState(false);
  const isEditingRef = useRef(false);
  isEditingRef.current = isEditing;

  // SSE: real-time essay change notifications
  const handleExternalEssayUpdate = useCallback((eventSlug: string) => {
    if (eventSlug !== slug) return;
    if (isEditingRef.current) {
      setExternalUpdate(true);
    }
  }, [slug]);

  useEssayEvents({ onExternalUpdate: handleExternalEssayUpdate });

  const handleReloadExternal = useCallback(() => {
    setExternalUpdate(false);
    setEditorRevision(r => r + 1);
    queryClient.invalidateQueries({ queryKey: ['essay', slug] });
  }, [slug, queryClient]);

  const handleDismissExternal = useCallback(() => {
    setExternalUpdate(false);
  }, []);

  const handleExitEdit = useCallback(() => {
    setIsEditing(false);
    setExternalUpdate(false);
    queryClient.invalidateQueries({ queryKey: ['essay', slug] });
  }, [slug, queryClient]);

  // Audio generation state
  const [hasAudio, setHasAudio] = useState<boolean>(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);

  useEffect(() => {
    if (!slug) return;
    fetch(`/api/public/essays/${slug}/audio/manifest.json`, { method: 'HEAD' })
      .then(r => setHasAudio(r.ok))
      .catch(() => setHasAudio(false));
  }, [slug]);

  // Fetch essay from API
  const {
    data: essay,
    isLoading: essayLoading,
    error: essayError,
  } = useQuery({
    queryKey: ['essay', slug],
    queryFn: () => getEssay(slug!),
    enabled: !!slug,
  });

  // Fetch comments for badge count
  const { data: comments = [] } = useQuery({
    queryKey: ['comments', slug],
    queryFn: () => getComments(slug!),
    enabled: !!slug && !!user,
  });

  // Fetch suggestions for badge count
  const { data: suggestions = [] } = useQuery({
    queryKey: ['suggestions', slug],
    queryFn: () => getSuggestions(slug!),
    enabled: !!slug && !!user,
  });

  // Fetch attachments for badge count
  const { data: attachments = [] } = useQuery({
    queryKey: ['attachments', slug],
    queryFn: () => getAttachments(slug!),
    enabled: !!slug && !!user,
  });

  // Fetch linked notes for badge count
  const { data: linkedNotes = [] } = useQuery<LinkedNoteFromEssay[]>({
    queryKey: ['linkedNotes', slug],
    queryFn: () => getLinkedNotes(slug!),
    enabled: !!slug && !!user,
  });

  // Fetch essay tags
  const { data: essayTags = [] } = useQuery({
    queryKey: ['essayTags', slug],
    queryFn: () => getEssayTags(slug!),
    enabled: !!slug && !!user,
  });

  // Fetch series info
  const { data: seriesInfo = [] } = useQuery({
    queryKey: ['essaySeries', slug],
    queryFn: () => getEssaySeriesInfo(slug!),
    enabled: !!slug && !!user,
  });

  // Badge counts
  const unresolvedCommentCount = comments.filter((c) => !c.resolved).length;
  const pendingSuggestionCount = suggestions.filter((s) => s.status === 'pending').length;
  const attachmentCount = attachments.length;
  const notesCount = linkedNotes.length;

  function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  const handleCommentsChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['comments', slug] });
  }, [queryClient, slug]);

  const handleSuggestionsChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['suggestions', slug] });
  }, [queryClient, slug]);

  const handleTextSelect = useCallback((selection: TextSelection | null) => {
    setTextSelection(selection);
  }, []);

  const handleCommentClick = useCallback((_comment: CommentWithReplies) => {
    navigate(`/reviewer/essays/${slug}/comments`);
  }, [navigate, slug]);

  const handleClosePopover = useCallback(() => {
    setTextSelection(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  const handleCommentCreated = useCallback(() => {
    handleCommentsChange();
    setTextSelection(null);
  }, [handleCommentsChange]);

  const handleSuggestionCreated = useCallback(() => {
    handleSuggestionsChange();
    setTextSelection(null);
  }, [handleSuggestionsChange]);

  const handleStatusChange = useCallback(
    (newStatus: EssayStatus) => {
      queryClient.setQueryData(['essay', slug], (old: Essay | undefined) => {
        if (!old) return old;
        return { ...old, status: newStatus, updatedAt: new Date().toISOString() };
      });
      queryClient.invalidateQueries({ queryKey: ['versions', slug] });
    },
    [queryClient, slug]
  );

  const handleGenerateAudio = useCallback(async () => {
    if (!slug) return;
    setGeneratingAudio(true);
    try {
      const result = await generateAudio(slug);
      setHasAudio(true);
      message.success(`Audio generated: ${result.segments} segments`);
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Audio generation failed');
    } finally {
      setGeneratingAudio(false);
    }
  }, [slug]);

  // Loading state
  if (essayLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (essayError) {
    return (
      <div>
        <Link to="/reviewer/essays">
          <Button icon={<ArrowLeftOutlined />} style={{ marginBottom: '24px' }}>
            Back to Essays
          </Button>
        </Link>
        <Alert
          message="Error loading essay"
          description={essayError instanceof Error ? essayError.message : 'An unknown error occurred'}
          type="error"
          showIcon
        />
      </div>
    );
  }

  // Not found state
  if (!essay) {
    return (
      <div>
        <Link to="/reviewer/essays">
          <Button icon={<ArrowLeftOutlined />} style={{ marginBottom: '24px' }}>
            Back to Essays
          </Button>
        </Link>
        <Alert message="Essay not found" description="The requested essay does not exist." type="warning" showIcon />
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Row gutter={24}>
        {/* Table of Contents sidebar */}
        <Col xs={0} lg={6}>
          <div style={{ position: 'sticky', top: 80 }}>
            <TableOfContents content={essay.content} />
          </div>
        </Col>

        {/* Main content area */}
        <Col xs={24} lg={18}>
          <article style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Back button and action buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', position: 'sticky', top: 64, zIndex: 10, background: 'var(--bg-base)', borderBottom: '1px solid var(--border-subtle)', padding: '8px 0' }}>
          <Link to="/reviewer/essays">
            <Button type="text" icon={<ArrowLeftOutlined />} style={{ paddingLeft: 0 }}>
              Back to Essays
            </Button>
          </Link>

          {/* Review action buttons — now link to sub-pages */}
          {user && (
            <Space>
              {/* Supporting Info */}
              <Badge count={attachmentCount + notesCount} size="small" offset={[-4, 4]}>
                <Link to={`/reviewer/essays/${slug}/info`}>
                  <Button icon={<PaperClipOutlined />}>
                    Info
                  </Button>
                </Link>
              </Badge>

              {/* Version History */}
              <Link to={`/reviewer/essays/${slug}/history`}>
                <Button icon={<HistoryOutlined />}>
                  History
                </Button>
              </Link>

              {/* Edit / View toggle (author only) */}
              {user?.role === 'author' && (
                <Button
                  icon={isEditing ? <EyeOutlined /> : <EditOutlined />}
                  onClick={() => isEditing ? handleExitEdit() : setIsEditing(true)}
                  style={isEditing ? { borderColor: 'var(--accent-gold)', color: 'var(--accent-gold)' } : undefined}
                >
                  {isEditing ? 'View' : 'Edit'}
                </Button>
              )}

              {/* Generate Audio button (author only) */}
              {user?.role === 'author' && !isEditing && (
                <Button
                  icon={<SoundOutlined />}
                  loading={generatingAudio}
                  onClick={handleGenerateAudio}
                  disabled={generatingAudio}
                >
                  {hasAudio ? 'Regenerate Audio' : 'Generate Audio'}
                </Button>
              )}

              {/* Suggestions */}
              <Badge count={pendingSuggestionCount} size="small" offset={[-4, 4]}>
                <Link to={`/reviewer/essays/${slug}/suggestions`}>
                  <Button icon={<BulbOutlined />}>
                    Suggestions
                  </Button>
                </Link>
              </Badge>

              {/* Comments */}
              <Badge count={unresolvedCommentCount} size="small" offset={[-4, 4]}>
                <Link to={`/reviewer/essays/${slug}/comments`}>
                  <Button type="primary" icon={<CommentOutlined />}>
                    Comments
                  </Button>
                </Link>
              </Badge>
            </Space>
          )}
        </div>

        {/* Essay header */}
        <header style={{ marginBottom: '32px' }}>
          {/* Status badge/dropdown */}
          <div style={{ marginBottom: '16px' }}>
            {user ? (
              <StatusDropdown
                status={essay.status}
                slug={slug!}
                user={user}
                onStatusChange={handleStatusChange}
              />
            ) : (
              <Tag>{essay.status}</Tag>
            )}
          </div>

          {/* Title */}
          <Title
            level={1}
            style={{
              fontFamily: 'var(--font-serif)',
              fontWeight: 400,
              marginBottom: '24px',
              lineHeight: 1.3,
            }}
          >
            {essay.title}
          </Title>

          {/* Meta information */}
          <Space size="large" wrap style={{ color: '#a0a0a0' }}>
            <Space size="small">
              <Avatar
                src={essay.author.avatarUrl}
                icon={!essay.author.avatarUrl && <UserOutlined />}
                size="small"
              />
              <Text type="secondary">{essay.author.name}</Text>
            </Space>
            <Space size="small">
              <CalendarOutlined />
              <Text type="secondary">Created {formatDate(essay.createdAt)}</Text>
            </Space>
            <Space size="small">
              <EditOutlined />
              <Text type="secondary">Updated {formatDate(essay.updatedAt)}</Text>
            </Space>
          </Space>

          {/* Series navigation */}
          {seriesInfo.length > 0 && (
            <div style={{ marginTop: 16 }}>
              {seriesInfo.map((info) => (
                <div
                  key={info.series.id}
                  style={{
                    background: 'var(--bg-elevated)',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginBottom: 8,
                  }}
                >
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <OrderedListOutlined style={{ color: 'var(--accent-gold)' }} />
                      <Link to={`/reviewer/series/${info.series.slug}`} style={{ color: 'var(--accent-gold-hover)' }}>
                        {info.series.name}
                      </Link>
                      <Text type="secondary">
                        ({info.orderIndex + 1} of {info.totalInSeries})
                      </Text>
                    </Space>
                    <Space>
                      {info.previousEssay && (
                        <Link to={`/reviewer/essays/${info.previousEssay.slug}`}>
                          <Button type="text" size="small">
                            ← Previous
                          </Button>
                        </Link>
                      )}
                      {info.nextEssay && (
                        <Link to={`/reviewer/essays/${info.nextEssay.slug}`}>
                          <Button type="text" size="small">
                            Next →
                          </Button>
                        </Link>
                      )}
                    </Space>
                  </Space>
                </div>
              ))}
            </div>
          )}

          {/* Category and Tags display */}
          {(essay.categoryId || essayTags.length > 0) && (
            <div style={{ marginTop: 12 }}>
              <Space wrap>
                {essay.categoryId && (
                  <Tag icon={<FolderOutlined />} color="gold">
                    Category {essay.categoryId}
                  </Tag>
                )}
                {essayTags.map((tag) => (
                  <Tag key={tag.id} icon={<TagsOutlined />}>
                    {tag.name}
                  </Tag>
                ))}
              </Space>
            </div>
          )}
        </header>

        <Divider style={{ borderColor: 'var(--border-primary)', marginBottom: '32px' }} />

        {/* Essay content: editor mode or read-only mode */}
        {isEditing ? (
          <EssayEditor
            slug={slug!}
            initialContent={essay.content}
            contentHash={essay.contentHash}
            editorRevision={editorRevision}
            onExternalUpdate={externalUpdate}
            onReloadExternal={handleReloadExternal}
            onDismissExternal={handleDismissExternal}
            onExitEdit={handleExitEdit}
          />
        ) : (
          <TextHighlighter
            content={essay.content}
            comments={comments}
            suggestions={suggestions}
            onTextSelect={user ? handleTextSelect : undefined}
            onCommentClick={handleCommentClick}
          />
        )}

        {/* Footer */}
        <Divider style={{ borderColor: 'var(--border-primary)', marginTop: '48px' }} />
        <footer style={{ textAlign: 'center', padding: '24px 0' }}>
          <Text type="secondary">
            Source: <code>{essay.sourcePath}</code>
          </Text>
        </footer>
          </article>
        </Col>
      </Row>

      {/* Inline comment popover (appears on text selection) */}
      {user && (
        <InlineCommentPopover
          selection={textSelection}
          essaySlug={slug!}
          currentUser={user}
          onClose={handleClosePopover}
          onCommentCreated={handleCommentCreated}
          onSuggestionCreated={handleSuggestionCreated}
        />
      )}
    </div>
  );
}

export default EssayDetailPage;
